import {
  COUNTRY_LOCALE_MAP,
  SITE_REGION_CODES,
  BLOCKED_REGION_CODES,
  getSiteBaseUrl,
  type SiteRegionCode,
} from './utils';

export const LOCALE_TO_REGION: Record<string, SiteRegionCode> = {
  'en-AU': 'au',
  'en-NZ': 'nz',
  'en-US': 'us',
  'en-IN': 'in',
  'en-au': 'au',
  'en-nz': 'nz',
  'en-us': 'us',
  'en-in': 'in',
  au: 'au',
  nz: 'nz',
  us: 'us',
  in: 'in',
};

export interface RegionalVariant {
  locale: string;
  path?: string | null;
  status?: string | null;
  discontinued?: boolean | null;
  noIndex?: boolean | null;
}

/**
 * Builds an absolute page URL for a specific region.
 * - 'au': uses NEXT_PUBLIC_SINGLE_REGION_URL (default: 'https://unimig.com.au') without /au prefix
 * - 'nz', 'us', 'in': uses NEXT_PUBLIC_WEBSITE_URL/{region} (default: 'https://unimig.com/{region}')
 */
export function buildRegionalPageUrl(
  regionCode?: string | null,
  path?: string | null
): string {
  const base = getSiteBaseUrl(regionCode);
  const cleanPath = (path || '').trim();

  if (!cleanPath || cleanPath === '/') {
    return `${base}/`;
  }

  const normalizedPath = cleanPath.startsWith('/')
    ? cleanPath
    : `/${cleanPath}`;

  // Ensure trailing slash before query parameters or at the end of directory routes
  const [pathAndHash, query] = normalizedPath.split('?');
  const [pathname, hash] = pathAndHash.split('#');
  const lastSegment = pathname.split('/').filter(Boolean).pop() || '';
  const isFile = lastSegment.includes('.');

  let finalPathname = pathname;
  if (!isFile && !finalPathname.endsWith('/')) {
    finalPathname = `${finalPathname}/`;
  }

  const queryPart = query !== undefined ? `?${query}` : '';
  const hashPart = hash !== undefined ? `#${hash}` : '';

  return `${base}${finalPathname}${queryPart}${hashPart}`;
}

/**
 * Generates reciprocal hreflang alternate language links.
 * Returns undefined if available in fewer than 2 countries/languages.
 */
export function buildHreflangLanguages(
  variants: RegionalVariant[]
): Record<string, string> | undefined {
  const validVariants = variants.filter((v) => {
    if (!v.path || v.path.includes('undefined')) return false;

    const normalizedRegion =
      LOCALE_TO_REGION[v.locale] ||
      LOCALE_TO_REGION[v.locale?.toLowerCase()?.trim()];
    if (!normalizedRegion) return false;

    if (!(SITE_REGION_CODES as readonly string[]).includes(normalizedRegion)) {
      return false;
    }

    if (BLOCKED_REGION_CODES.includes(normalizedRegion)) {
      return false;
    }

    if (
      v.status !== undefined &&
      v.status !== null &&
      v.status !== 'published'
    ) {
      return false;
    }

    if (v.discontinued === true || v.noIndex === true) {
      return false;
    }

    return true;
  });

  const languages: Record<string, string> = {};

  for (const v of validVariants) {
    const region =
      LOCALE_TO_REGION[v.locale] ||
      LOCALE_TO_REGION[v.locale?.toLowerCase()?.trim()]!;
    const hreflangCode = COUNTRY_LOCALE_MAP[region];
    if (hreflangCode) {
      languages[hreflangCode] = buildRegionalPageUrl(region, v.path);
    }
  }

  if (Object.keys(languages).length < 2) {
    return undefined;
  }

  if (languages['en-AU']) {
    languages['x-default'] = languages['en-AU'];
  } else if (validVariants.length > 0) {
    const firstRegion =
      LOCALE_TO_REGION[validVariants[0].locale] ||
      LOCALE_TO_REGION[validVariants[0].locale?.toLowerCase()?.trim()]!;
    languages['x-default'] = buildRegionalPageUrl(
      firstRegion,
      validVariants[0].path
    );
  }

  return languages;
}

/**
 * Generates hreflang alternate links for global static pages.
 */
export function getStaticPageHreflang(
  path: string,
  options?: {
    excludeRegions?: string[];
  }
): Record<string, string> | undefined {
  const excluded = new Set(
    (options?.excludeRegions || []).map((r) => r.toLowerCase().trim())
  );

  const variants: RegionalVariant[] = (SITE_REGION_CODES as readonly string[])
    .filter((code) => !excluded.has(code))
    .map((code) => ({
      locale: COUNTRY_LOCALE_MAP[code] || code,
      path,
      status: 'published',
    }));

  return buildHreflangLanguages(variants);
}
